import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchMe } from './api.js';
import { AudioCapture } from './audio/capture.js';
import { loadSubtitles, pruneSubtitlesBefore } from './db/idb.js';
import { useWakeLock } from './hooks/useWakeLock.js';
import { Admin } from './screens/Admin.js';
import { DocsQa } from './screens/DocsQa.js';
import { Lectures } from './screens/Lectures.js';
import { Login } from './screens/Login.js';
import { PhotoMode } from './screens/PhotoMode.js';
import { QuestionMode } from './screens/QuestionMode.js';
import { Setup, type SetupResult } from './screens/Setup.js';
import { Translating } from './screens/Translating.js';
import { useSessionStore } from './state/session.js';
import { TranslationWsClient } from './ws/client.js';

type Phase = 'loading' | 'login' | 'setup' | 'translating' | 'lectures' | 'docs-qa';

export function App(): JSX.Element {
  // ルーターを入れるほどの画面数ではないので、/admin だけパスで振り分ける。
  // パスはページ読み込み中に変わらないため、この早期 return がフック順を壊すことはない。
  if (window.location.pathname === '/admin') {
    return <Admin />;
  }
  return <MainApp />;
}

function MainApp(): JSX.Element {
  const [phase, setPhase] = useState<Phase>('loading');
  const [fatalError, setFatalError] = useState<string>();

  const captureRef = useRef<AudioCapture | null>(null);
  const clientRef = useRef<TranslationWsClient | null>(null);
  /** 質問モード(日→中)のクライアント。開いている間は音声フレームをこちらへ流す。 */
  const questionClientRef = useRef<TranslationWsClient | null>(null);
  const [questionActive, setQuestionActive] = useState(false);
  const [photoActive, setPhotoActive] = useState(false);
  /** 質問モードが本編と同じ講義メモへ書き込むための情報。 */
  const sessionInfoRef = useRef<{
    clientSessionId: string;
    device: { deviceId?: string; deviceName?: string };
  }>({ clientSessionId: '', device: {} });
  // 音量は毎秒数十回更新される。ref に置いて再描画を起こさない。
  const levelRef = useRef({ rms: 0, tooQuiet: false, clipping: false });

  const handleServerMessage = useSessionStore((s) => s.handleServerMessage);
  const setConnection = useSessionStore((s) => s.setConnection);
  const markReconnected = useSessionStore((s) => s.markReconnected);
  const markBackgroundReturn = useSessionStore((s) => s.markBackgroundReturn);
  const beginSession = useSessionStore((s) => s.beginSession);
  const endSession = useSessionStore((s) => s.endSession);
  const loadHistory = useSessionStore((s) => s.loadHistory);

  const { supported: wakeLockSupported } = useWakeLock(phase === 'translating');

  // 起動時: ログイン状態の確認と、前回の字幕履歴の復元。
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const me = await fetchMe();
        if (cancelled) return;
        setPhase(me.authenticated ? 'setup' : 'login');
      } catch {
        if (!cancelled) setPhase('login');
      }

      try {
        const records = await loadSubtitles();
        if (!cancelled && records.length > 0) loadHistory(records);
      } catch {
        // 履歴の復元に失敗しても翻訳自体は使える。黙って続行する。
      }

      // 履歴の掃除は復元の後に、画面を待たせず裏で行う。
      // 7日より古い分は削除(サーバーの講義メモに保存済み)。
      try {
        const cutoff = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
        void pruneSubtitlesBefore(cutoff);
      } catch {
        // 掃除に失敗しても起動は続行する。次回起動時に再試行される。
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadHistory]);

  const stopEverything = useCallback(async (): Promise<void> => {
    // 音声の取得を先に止める。ソケットを先に閉じると送信先の無いフレームが出る。
    // ただし AudioContext.close() は長時間セッション後のモバイル Safari で稀に
    // 永遠に解決しないことがある(現地報告: 46分後に停止ボタンが無反応)。
    // 2秒で見切りを付けて先へ進む。マイクトラック自体は stop() 内で先に止まっている。
    const capture = captureRef.current;
    captureRef.current = null;
    if (capture) {
      await Promise.race([capture.stop(), new Promise((r) => setTimeout(r, 2000))]);
    }
    setQuestionActive(false);
    questionClientRef.current?.stop();
    questionClientRef.current = null;
    clientRef.current?.stop();
    clientRef.current = null;
    endSession();
  }, [endSession]);

  // タブを閉じられてもマイクとソケットを解放する。
  useEffect(() => {
    const handleUnload = (): void => {
      void captureRef.current?.stop();
      clientRef.current?.stop();
    };
    window.addEventListener('pagehide', handleUnload);
    return () => window.removeEventListener('pagehide', handleUnload);
  }, []);

  // スマホの戻る操作(スワイプ/戻るボタン)でアプリ内の画面遷移を行う。
  // 講義メモ系の画面は history に state を積んでおり、pop でその状態に合わせる。
  useEffect(() => {
    const handlePop = (event: PopStateEvent): void => {
      const screen = (event.state as { screen?: string } | null)?.screen;
      if (screen === 'lectures' || screen === 'lecture-detail') {
        setPhase('lectures');
      } else if (screen === 'docs-qa') {
        setPhase('docs-qa');
      } else {
        // 履歴の底(state なし)= 設定画面。翻訳中は履歴を積まないため影響しない。
        setPhase((current) =>
          current === 'lectures' || current === 'docs-qa' ? 'setup' : current,
        );
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []);

  // 翻訳中にバックグラウンドへ行くと、ブラウザの仕様でマイクが止まり音声が欠落する。
  // 復帰時に区切りを入れて、欠落が「なかったこと」にならないようにする。
  useEffect(() => {
    if (phase !== 'translating') return;
    const handleVisibility = (): void => {
      if (document.visibilityState === 'visible') markBackgroundReturn();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [phase, markBackgroundReturn]);

  const handleStart = async (setup: SetupResult): Promise<void> => {
    setFatalError(undefined);
    const clientSessionId = crypto.randomUUID();
    sessionInfoRef.current = {
      clientSessionId,
      device: { deviceId: setup.ownDeviceId, deviceName: setup.deviceName },
    };
    beginSession(setup.profiles, clientSessionId);

    const client = new TranslationWsClient({
      onMessage: handleServerMessage,
      onStateChange: setConnection,
      onReconnected: markReconnected,
    });
    clientRef.current = client;
    client.start(setup.profiles, clientSessionId, setup.mode, { deviceId: setup.ownDeviceId, deviceName: setup.deviceName });

    const capture = new AudioCapture();
    captureRef.current = capture;

    try {
      await capture.start(
        { deviceId: setup.deviceId, noiseSuppression: setup.noiseSuppression, gain: setup.gain },
        {
          onFrame: (frame) => {
            // 質問モード中は日本語音声を質問用セッションだけに流す。
            // 本編(zh→ja)へ流すと日本語が誤認識され、講義メモが汚れるため。
            const question = questionClientRef.current;
            if (question) {
              question.sendAudio(frame);
              return;
            }
            // 切断中のフレームは client 側で捨てられる。ここでは貯めない。
            client.sendAudio(frame);
          },
          onLevel: (level) => {
            levelRef.current = level;
          },
          onError: (message) => setFatalError(message),
        },
      );
      setPhase('translating');
    } catch (error) {
      // マイクが開けなければ翻訳は成立しない。設定画面へ戻す。
      client.stop();
      clientRef.current = null;
      captureRef.current = null;
      setFatalError(error instanceof Error ? error.message : 'マイクを開始できませんでした。');
    }
  };

  const handleStop = (): void => {
    void stopEverything().then(() => setPhase('setup'));
  };

  if (phase === 'loading') {
    return <div className="screen">読み込んでいます…</div>;
  }

  if (phase === 'login') {
    return <Login onSuccess={() => setPhase('setup')} />;
  }

  if (phase === 'translating') {
    return (
      <>
        <Translating
          levelSource={levelRef}
          onStop={handleStop}
          onQuestion={() => setQuestionActive(true)}
          onPhoto={() => setPhotoActive(true)}
          wakeLockSupported={wakeLockSupported}
        />
        {photoActive && (
          <PhotoMode
            clientSessionId={sessionInfoRef.current.clientSessionId}
            device={sessionInfoRef.current.device}
            onClose={() => setPhotoActive(false)}
          />
        )}
        {questionActive && (
          <QuestionMode
            clientSessionId={sessionInfoRef.current.clientSessionId}
            device={sessionInfoRef.current.device}
            onClientChange={(client) => {
              questionClientRef.current = client;
            }}
            onClose={() => setQuestionActive(false)}
          />
        )}
      </>
    );
  }

  if (phase === 'lectures') {
    // 戻るボタンも history.back() に統一し、スワイプ/ブラウザ戻ると同じ経路にする。
    return <Lectures onBack={() => window.history.back()} />;
  }

  if (phase === 'docs-qa') {
    return <DocsQa onBack={() => window.history.back()} />;
  }

  return (
    <>
      {fatalError && (
        <div className="screen" style={{ paddingBottom: 0 }}>
          <div className="notice error">{fatalError}</div>
        </div>
      )}
      <Setup
        onStart={(setup) => void handleStart(setup)}
        onOpenLectures={() => {
          // 履歴に積むことで、スマホの戻る操作(スワイプ/戻るボタン)でも設定画面へ戻れる。
          window.history.pushState({ screen: 'lectures' }, '');
          setPhase('lectures');
        }}
        onOpenDocsQa={() => {
          window.history.pushState({ screen: 'docs-qa' }, '');
          setPhase('docs-qa');
        }}
      />
    </>
  );
}
